/**
 * Tenant Add-ons tab (TMMPDM-0019): the tenant's add-ons, each expanding to its period charges.
 * Row actions follow the add-on's status — OFFERED → Withdraw; ACTIVE / PAST_DUE → Cancel… — and
 * a charge that isn't settled or written off can be waived. The SCA secret a charge may carry is
 * for the tenant admin's accept flow and is never read here. No "Revenue this year" column:
 * tmm-api writes `revenueYearMinor` on the fleet list only, so here it would always read "—".
 */
import { Fragment, useEffect, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { ConfirmDialog, EmptyState, ErrorState, useNotify } from '@/components/page';
import { Amount } from '@/components/ui';
import { adminApi } from '@/api/tmm/admin';
import { errorCopy } from '@/api/errors';
import type { ChargeView, TenantAddonView } from '@/api/tmm/types';
import {
  BILLING_MODE_FALLBACK,
  CHARGE_MODE_FALLBACK,
  CHARGE_STATUS_FALLBACK,
  PER_PERIOD_FALLBACK,
  TENANT_ADDON_STATUS_FALLBACK,
  canWaiveCharge,
} from '@/routes/addons/addonFormat';
import { CancelAddonDialog } from './CancelAddonDialog';
import { OfferAddonDialog } from './OfferAddonDialog';
import { WaiveChargeDialog } from './WaiveChargeDialog';
import { formatDate } from '@/format';
import { StatusChip } from '@/status';

const COLUMNS = 10;

interface TenantAddonsPanelProps {
  slug: string;
}

export function TenantAddonsPanel({ slug }: TenantAddonsPanelProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [reloadToken, setReloadToken] = useState(0);
  const [rows, setRows] = useState<TenantAddonView[] | null>(null);
  const [loadFailure, setLoadFailure] = useState<{ cause: unknown } | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [offering, setOffering] = useState(false);
  const [withdrawing, setWithdrawing] = useState<TenantAddonView | null>(null);
  const [withdrawBusy, setWithdrawBusy] = useState(false);
  // A refused withdrawal is said inside its confirm, which stays open (TMM-B4).
  const [withdrawError, setWithdrawError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<TenantAddonView | null>(null);
  const [waiving, setWaiving] = useState<{ addonId: number; chargeId: number } | null>(null);

  const reload = () => setReloadToken((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listTenantAddons(slug)
      .then((list) => {
        if (cancelled) return;
        setRows(list.addons);
        setLoadFailure(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setRows(null);
        setLoadFailure({ cause: err });
      });
    return () => {
      cancelled = true;
    };
  }, [slug, reloadToken]);

  const withdraw = async (addon: TenantAddonView) => {
    setWithdrawError(null);
    setWithdrawBusy(true);
    try {
      await adminApi.withdrawAddon(slug, addon.id);
      setWithdrawing(null);
      reload();
    } catch (err: unknown) {
      setWithdrawError(errorCopy(err, t, t('addons.actionError', 'Could not update the add-on.')));
    } finally {
      setWithdrawBusy(false);
    }
  };

  if (loadFailure) {
    return (
      <ErrorState
        error={loadFailure.cause}
        message={t('addons.tenant.loadError', 'Could not load add-ons.')}
        onRetry={reload}
      />
    );
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Button variant="contained" onClick={() => setOffering(true)}>
          {t('addons.actions.offer', 'Offer add-on…')}
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label={t('addons.tenant.tableAria', 'Tenant add-ons')}>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>{t('addons.col.addon', 'Add-on')}</TableCell>
              <TableCell>{t('addons.col.status', 'Status')}</TableCell>
              <TableCell>{t('addons.col.billingMode', 'Billing mode')}</TableCell>
              <TableCell>{t('addons.col.price', 'Price per period')}</TableCell>
              <TableCell>{t('addons.col.term', 'Term')}</TableCell>
              <TableCell>{t('addons.col.autoRenew', 'Auto-renew')}</TableCell>
              <TableCell>{t('addons.col.offered', 'Offered')}</TableCell>
              <TableCell>{t('addons.col.accepted', 'Accepted')}</TableCell>
              <TableCell>{t('addons.col.actions', 'Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows === null ? (
              <TableRow>
                <TableCell colSpan={COLUMNS} align="center">
                  {t('common.loading', 'Loading')}
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMNS} align="center">
                  <EmptyState title={t('addons.tenant.empty', 'No add-ons for this tenant.')} />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const open = expanded === row.id;
                return (
                  <Fragment key={row.id}>
                    <TableRow>
                      <TableCell>
                        <IconButton
                          size="small"
                          aria-label={
                            open
                              ? t('addons.tenant.collapse', 'Hide charges for {{name}}', {
                                  name: row.name,
                                })
                              : t('addons.tenant.expand', 'Show charges for {{name}}', {
                                  name: row.name,
                                })
                          }
                          onClick={() => setExpanded(open ? null : row.id)}
                        >
                          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>
                        <StatusChip
                          status={row.status}
                          label={t(
                            `addons.status.${row.status}`,
                            TENANT_ADDON_STATUS_FALLBACK[row.status],
                          )}
                        />
                        {row.cancelAtTermEnd ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {t('addons.tenant.cancels', 'Cancels {{date}}', {
                              date: formatDate(row.termEnd, locale),
                            })}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {row.billingMode
                          ? t(
                              `addons.billingMode.${row.billingMode}`,
                              BILLING_MODE_FALLBACK[row.billingMode],
                            )
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Amount minor={row.priceMinor} currency={row.currency} locale={locale} />{' '}
                        {t(`addons.perPeriod.${row.period}`, PER_PERIOD_FALLBACK[row.period])}
                      </TableCell>
                      <TableCell>
                        {row.termStart || row.termEnd
                          ? `${formatDate(row.termStart, locale)} – ${formatDate(row.termEnd, locale)}`
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {row.autoRenew ? t('addons.yes', 'Yes') : t('addons.no', 'No')}
                      </TableCell>
                      <TableCell>{formatDate(row.offeredAt, locale)}</TableCell>
                      <TableCell>{formatDate(row.acceptedAt, locale)}</TableCell>
                      <TableCell>
                        {row.status === 'OFFERED' ? (
                          <Button size="small" onClick={() => setWithdrawing(row)}>
                            {t('addons.actions.withdraw', 'Withdraw')}
                          </Button>
                        ) : null}
                        {row.status === 'ACTIVE' || row.status === 'PAST_DUE' ? (
                          <Button size="small" color="error" onClick={() => setCancelling(row)}>
                            {t('addons.actions.cancelAddon', 'Cancel…')}
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={COLUMNS}
                        sx={{ py: 0, borderBottom: open ? undefined : 'none' }}
                      >
                        <Collapse in={open} timeout="auto" unmountOnExit>
                          <ChargesTable
                            addon={row}
                            locale={locale}
                            onWaive={(chargeId) => setWaiving({ addonId: row.id, chargeId })}
                          />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <OfferAddonDialog
        open={offering}
        slug={slug}
        onClose={() => setOffering(false)}
        onOffered={() => {
          setOffering(false);
          reload();
        }}
      />
      <ConfirmDialog
        open={withdrawing !== null}
        title={t('addons.withdraw.title', 'Withdraw the offer?')}
        consequence={t(
          'addons.withdraw.confirm',
          'Withdraw the offer of {{name}}? The tenant can no longer accept it.',
          { name: withdrawing?.name ?? '' },
        )}
        confirmLabel={t('addons.actions.withdraw', 'Withdraw')}
        destructive
        busy={withdrawBusy}
        error={withdrawError}
        onCancel={() => {
          setWithdrawing(null);
          setWithdrawError(null);
        }}
        onConfirm={() => {
          if (withdrawing) void withdraw(withdrawing);
        }}
      />
      <CancelAddonDialog
        open={cancelling !== null}
        slug={slug}
        addon={cancelling}
        onClose={() => setCancelling(null)}
        onDone={() => {
          setCancelling(null);
          reload();
        }}
      />
      <WaiveChargeDialog
        open={waiving !== null}
        slug={slug}
        addonId={waiving?.addonId ?? 0}
        chargeId={waiving?.chargeId ?? 0}
        onClose={() => setWaiving(null)}
        onDone={() => {
          setWaiving(null);
          reload();
        }}
      />
    </Stack>
  );
}

/** What a person copies for support: the charge's identifiers and the raw `lastError` (English `key: value` lines; an unsent value is `-`). */
function chargeSupportDetails(addon: TenantAddonView, charge: ChargeView): string {
  return [
    `tenantAddonId: ${addon.id}`,
    `addonKind: ${addon.addonKind}`,
    `chargeId: ${charge.id}`,
    `period: ${charge.periodStart} to ${charge.periodEnd}`,
    `status: ${charge.status}`,
    `mode: ${charge.mode}`,
    `attempt: ${charge.attempt}`,
    `lastError: ${charge.lastError === undefined || charge.lastError === '' ? '-' : charge.lastError}`,
  ].join('\n');
}

function ChargesTable({
  addon,
  locale,
  onWaive,
}: {
  addon: TenantAddonView;
  locale: string;
  onWaive: (chargeId: number) => void;
}) {
  const { t } = useCopy();
  const notify = useNotify();
  // TMM-B3: the raw `lastError` is only copied for support, never shown.
  const copyDetails = async (charge: ChargeView) => {
    try {
      await navigator.clipboard.writeText(chargeSupportDetails(addon, charge));
      notify.success(t('components.copied', 'Copied'));
    } catch {
      notify.info(t('components.copyFailed', "Couldn't copy — select the text instead."));
    }
  };
  if (addon.charges.length === 0) {
    return <EmptyState title={t('addons.charges.empty', 'No charges yet.')} />;
  }
  return (
    <Table
      size="small"
      aria-label={t('addons.charges.tableAria', 'Charges for {{name}}', { name: addon.name })}
      sx={{ my: 1 }}
    >
      <TableHead>
        <TableRow>
          <TableCell>{t('addons.charges.col.period', 'Period')}</TableCell>
          <TableCell>{t('addons.charges.col.amount', 'Amount')}</TableCell>
          <TableCell>{t('addons.charges.col.mode', 'Mode')}</TableCell>
          <TableCell>{t('addons.charges.col.status', 'Status')}</TableCell>
          <TableCell>{t('addons.charges.col.card', 'Card')}</TableCell>
          <TableCell>{t('addons.charges.col.attempt', 'Attempt')}</TableCell>
          <TableCell>{t('addons.charges.col.lastError', 'Last error')}</TableCell>
          <TableCell>{t('addons.charges.col.actions', 'Actions')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {addon.charges.map((charge) => (
          <TableRow key={charge.id}>
            <TableCell>
              {formatDate(charge.periodStart, locale)} – {formatDate(charge.periodEnd, locale)}
            </TableCell>
            <TableCell>
              <Amount minor={charge.amountMinor} currency={charge.currency} locale={locale} />
            </TableCell>
            <TableCell>
              {t(`addons.chargeMode.${charge.mode}`, CHARGE_MODE_FALLBACK[charge.mode])}
            </TableCell>
            <TableCell>
              <StatusChip
                status={charge.status}
                label={t(
                  `addons.chargeStatus.${charge.status}`,
                  CHARGE_STATUS_FALLBACK[charge.status],
                )}
              />
            </TableCell>
            <TableCell>{charge.maskedCard ?? '—'}</TableCell>
            <TableCell>{charge.attempt}</TableCell>
            <TableCell>
              {charge.status !== 'FAILED' &&
              (charge.lastError === undefined || charge.lastError === '') ? (
                '—'
              ) : (
                <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                  {charge.status === 'FAILED' ? (
                    <Box component="span">
                      {t('components.lastAttemptFailed', 'The last attempt failed.')}
                    </Box>
                  ) : null}
                  {charge.lastError === undefined || charge.lastError === '' ? null : (
                    <Button
                      size="small"
                      variant="outlined"
                      aria-label={t(
                        'addons.charges.copyDetailsAria',
                        'Copy details for support for charge {{id, number}}',
                        { id: charge.id },
                      )}
                      onClick={() => void copyDetails(charge)}
                    >
                      {t('components.copyDetails', 'Copy details for support')}
                    </Button>
                  )}
                </Stack>
              )}
            </TableCell>
            <TableCell>
              {canWaiveCharge(charge.status) ? (
                <Button size="small" onClick={() => onWaive(charge.id)}>
                  {t('addons.actions.waive', 'Waive…')}
                </Button>
              ) : null}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
